'use client';

import { Form, Formik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import OTPInput from 'react-otp-input';
import LoginOptions from 'src/components/app/auth/components/@shared/login-options';
import Props from 'src/components/app/auth/components/otp/type';

import Container from '@/components/@base/container';
import { Input } from '@/components/@base/input';
import AuthForm from '@/components/app/auth/components/@shared/auth-form';
import { LOGIN_BY } from '@/components/app/auth/components/@shared/login-options/type';
import ResendOtp from '@/components/app/auth/components/@shared/resend-otp';

import { STEP, useAuthStepStore } from '@/store/auth.store';

import { pageLevelLocalization } from '@/constant/localization';

const { login: loginLocalization } = pageLevelLocalization;

const convertToEnglishNumbers = (str: string): string => {
  const persianNumbers = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  const arabicNumbers = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  const englishNumbers = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

  return str
    .split('')
    .map((char) => {
      const persianIndex = persianNumbers.indexOf(char);
      const arabicIndex = arabicNumbers.indexOf(char);

      if (persianIndex !== -1) return englishNumbers[persianIndex];
      if (arabicIndex !== -1) return englishNumbers[arabicIndex];
      return char;
    })
    .join('');
};

export default function OtpForm({
  onSubmit,
  handleSendCode,
  isLoading,
  sentToBale = false,
}: Props) {
  const [otp, setOtp] = useState('');
  const { phoneNumber, setStep } = useAuthStepStore();
  const backHandler = () => {
    setStep(STEP.AUTH_STEP_START);
  };
  const [isWebOtpSupported, setIsWebOtpSupported] = useState(false);
  const webOtpAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const isSupported = typeof window !== 'undefined' && 'OTPCredential' in window;
    setIsWebOtpSupported(isSupported);

    if (isSupported) {
      let metaTag = document.querySelector('meta[name="otp-credential"]');
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute('name', 'otp-credential');
        metaTag.setAttribute('content', 'required');
        document.head.appendChild(metaTag);
      }
    }

    return () => {
      if (webOtpAbortRef.current) webOtpAbortRef.current.abort();
    };
  }, []);

  const requestWebOtp = () => {
    if (!isWebOtpSupported) return;

    if (webOtpAbortRef.current) webOtpAbortRef.current.abort();
    const ac = new AbortController();
    webOtpAbortRef.current = ac;

    navigator.credentials
      .get({
        otp: { transport: ['sms'] },
        signal: ac.signal,
      } as any)
      .then((res: any) => {
        if (res?.code) {
          const rawCode = String(res.code).trim();
          const normalized = convertToEnglishNumbers(rawCode).replace(/\D/g, '').slice(0, 5);
          if (normalized && normalized.length === 5) {
            setOtp(normalized);
            setTimeout(() => {
              const firstInput = document.querySelector(
                'input[autocomplete="one-time-code"]',
              ) as HTMLInputElement;
              if (firstInput) {
                firstInput.focus();
                firstInput.blur();
              }
            }, 100);
          }
        }
      })
      .catch((err) => {
        console.log('WebOTP error:', err);
      });
  };

  const focusFirstOtpInput = () => {
    try {
      const el = document.querySelector(
        'input[autocomplete="one-time-code"]',
      ) as HTMLInputElement | null;
      el?.focus();
    } catch {}
  };

  const startWebOtpFlow = () => {
    focusFirstOtpInput();
    requestWebOtp();
  };

  const handleOtpChange = (value: string) => {
    const englishValue = convertToEnglishNumbers(value);
    const numericValue = englishValue.replace(/\D/g, '');
    setOtp(numericValue);
  };

  const onSubmitHandler = (otp) => {
    onSubmit(otp, () => setOtp(''));
  };

  useEffect(() => {
    if (otp.length === 5) {
      onSubmitHandler(otp);
    }
  }, [otp]);
  return (
    <Formik
      validateOnChange={false}
      validateOnBlur={false}
      initialValues={{
        otp: null,
      }}
      onSubmit={(values) => onSubmitHandler(otp)}
    >
      {({ values, handleChange, setFieldValue }) => {
        return (
          <Form>
            <AuthForm
              title={loginLocalization.enterOtp}
              submitText={loginLocalization.otpVerification}
              phoneNumber={phoneNumber}
              isLoading={isLoading}
              sentToBale={sentToBale}
              backHandler={backHandler}
              actions={
                <Container center className='items-end justify-between pt-5'>
                  <ResendOtp
                    handleSendCode={() => {
                      handleSendCode();
                      setOtp('');
                      startWebOtpFlow();
                    }}
                  />
                  <LoginOptions loginBy={LOGIN_BY.PASSWORD} />
                </Container>
              }
            >
              <OTPInput
                value={otp}
                onChange={handleOtpChange}
                numInputs={5}
                inputType='text'
                shouldAutoFocus
                containerStyle={{ display: 'flex', gap: 12, direction: 'ltr' }}
                renderInput={(props, index) => (
                  <Input
                    {...props}
                    autoComplete='one-time-code'
                    onFocus={requestWebOtp}
                    inputMode='numeric'
                    className='h-[60px] w-[60px]! text-2xl focus:border-[#FFC87B] focus:shadow-[0px_2px_6px_0px_rgba(255,150,6,0.19)]'
                  />
                )}
              />
            </AuthForm>
          </Form>
        );
      }}
    </Formik>
  );
}
